import {Action, Attributes, Card, DataStatus, EmptyStatus, openDialog} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {isEmpty, isNotNil} from 'ramda';

import {useGetTelephonyLinesQuery, useGetTwilioAccountQuery} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {usePermissions} from '@dms/shared';

import {buildArray} from 'shared/web';

import {CreateTelephonyLineForm} from './components/CreateTelephonyLineForm';
import {TelephonyLineCard} from './components/TelephonyLineCard';
import {TelephonyRecordingSettingsCard} from './components/TelephonyRecordingSettingsCard';
import {TelephonyUnattachedNumbersCard} from './components/TelephonyUnattachedNumbersCard';

export function TelephonySettings() {
  const {
    data: twilioAccount,
    isLoading: isTwilioAccountLoading,
    isError: isTwilioAccountError,
  } = useGetTwilioAccountQuery();

  const isProvisioned = isNotNil(twilioAccount?.twilioAccountSid);

  const {
    data: lines,
    isLoading: areLinesLoading,
    isError: areLinesError,
  } = useGetTelephonyLinesQuery(undefined, {skip: !isProvisioned});

  const [canWriteTelephonySettings, canReadTelephonySettings] = usePermissions({
    permissionKeys: ['telephonySettingsWrite', 'telephonySettingsRead'],
  });

  const handleCreateLine = () =>
    openDialog(<CreateTelephonyLineForm data-testid={testIds.settings.telephony('createLine')} />, {
      id: 'createTelephonyLineDialog',
      title: i18n.t('page.settings.telephony.newLine'),
      size: 'small',
    });

  const canCreateLine = isProvisioned && canWriteTelephonySettings;

  const headerActions = buildArray<Action>().when(canCreateLine, {
    type: 'button',
    title: i18n.t('page.settings.telephony.newLine'),
    onClick: handleCreateLine,
    'data-testid': testIds.settings.telephony('newLine'),
  });

  const emptyStateAction = canCreateLine
    ? {
        title: i18n.t('page.settings.telephony.newLine'),
        leftIcon: 'content/add_circle' as const,
        variant: 'link' as const,
        onClick: handleCreateLine,
      }
    : undefined;

  return (
    <SettingsTemplate
      data-testid={testIds.settings.telephony('settingsTemplate')}
      header={{title: i18n.t('page.settings.telephony.title'), actions: headerActions}}
      isLoading={isTwilioAccountLoading}
      isError={isTwilioAccountError}
    >
      <SettingsSection>
        {isProvisioned ? (
          <VStack spacing={4}>
            <DataStatus
              isLoading={areLinesLoading}
              isError={areLinesError}
              isEmpty={isEmpty(lines?.lines ?? [])}
              emptyMessage={i18n.t('page.settings.telephony.notEnabledHeadline')}
              emptySubheadline={i18n.t('page.settings.telephony.notEnabledSubheadline')}
              action={emptyStateAction}
              minHeight={60}
              data-testid={testIds.settings.telephony('lines')}
            >
              <VStack spacing={4}>
                {lines?.lines.map((line, index) => (
                  <TelephonyLineCard
                    key={line.id}
                    line={line}
                    data-testid={testIds.settings.telephony(`line-[${index}]`)}
                  />
                ))}
              </VStack>
            </DataStatus>
            <TelephonyUnattachedNumbersCard
              data-testid={testIds.settings.telephony('unattachedNumbersCard')}
            />
            <Show when={canReadTelephonySettings}>
              <TelephonyRecordingSettingsCard
                data-testid={testIds.settings.telephony('recordingSettings')}
              />
            </Show>
            <Card title={i18n.t('page.settings.telephony.accountCardTitle')}>
              <Attributes
                size="quarter"
                rows={[
                  {
                    label: i18n.t('page.settings.telephony.subaccountSid'),
                    value: twilioAccount?.twilioAccountSid,
                  },
                  {
                    label: i18n.t('entity.bank.labels.accountName'),
                    value: twilioAccount?.friendlyName,
                  },
                  {
                    label: i18n.t('general.labels.status'),
                    value: twilioAccount?.status,
                  },
                ]}
                data-testid={testIds.settings.telephony('accountAttributes')}
              />
            </Card>
          </VStack>
        ) : (
          <EmptyStatus
            headline={i18n.t('page.settings.telephony.notProvisionedHeadline')}
            subheadline={i18n.t('page.settings.telephony.notProvisionedSubheadline')}
            data-testid={testIds.settings.telephony('emptyStatus')}
          />
        )}
      </SettingsSection>
    </SettingsTemplate>
  );
}
